import { createSelector } from '@reduxjs/toolkit';
import { selectCmsFromBootstrap, selectConfigFromBootstrap, selectGeoFromBootstrap } from 'store/slice/bootstrap';
import { PageContentStatus } from 'core/type/PageContentStatus';
import { BootstrapConfig, CmsValue, Geo } from 'core/type/BootstrapConfig.types';
import { selectPageContentStatus, selectSearchState, selectIsScrolling } from 'component/page/search/store/search.selectors';
import { getSearchPageFromCityIdUrl } from 'core/utils/activityUrlGenerator';
import { routeTo, setLocationSearch } from 'store/slice/navigation';
import { setSearchStateFromQueryParams } from 'component/page/search/store/search';
import { ActivitiesSeeAllActivitiesClickEventActivitiesFieldsV1 } from '@agoda/analytics-messages-definition-activities/events.split';
import { ActivitiesSeeAllActivitiesClickEventActivitiesManifestV1 } from '@agoda/analytics-messages-definition-activities/manifest/events';
import { trackElementEvent } from 'core/messaging/analytics/analytics';
import { QueryString } from 'core/utils/queryString/queryString';
import { Breakpoint, ScreenSize, selectBreakpoint, selectScreenSize } from 'store/slice/screen';

interface StateProps {
    config: BootstrapConfig;
    cms: CmsValue;
    geo?: Geo;
    contentStatus: PageContentStatus;
    isLoading: boolean;
    filteredResultNumber: number;
    pageTypeId: number;
    isScrolling: boolean;
    screenSize: ScreenSize;
    breakpoint: Breakpoint;
}

interface DispatchProps {
    onLocationSearchChange: (locationSearch: string) => void;
    goToSsrPage: (geo: Geo) => void;
}

export type CityPageProps = StateProps & DispatchProps;

export const mapStateToProps = createSelector(
    selectConfigFromBootstrap,
    selectCmsFromBootstrap,
    selectGeoFromBootstrap,
    selectSearchState,
    selectPageContentStatus,
    selectIsScrolling,
    selectScreenSize,
    selectBreakpoint,
    (config, cms, geo, state, contentStatus, isScrolling, screenSize, breakpoint: Breakpoint): StateProps => {
        const filteredResultNumber = state.searchResponse?.result?.resultInfo?.totalFilteredActivities || 0;
        return {
            config,
            cms,
            geo,
            contentStatus,
            filteredResultNumber,
            isScrolling,
            isLoading: state.isLoading,
            pageTypeId: config.pageConfig.pageTypeId,
            screenSize,
            breakpoint,
        };
    },
);

export const mapDispatchToProps = (dispatch: Function): DispatchProps => ({
    onLocationSearchChange: (locationSearch: string) => {
        const query = new QueryString(locationSearch);
        // we MUST remove cityId from the query string to avoid side effect
        if (query.have('cityId')) {
            query.delete('cityId');
            // setLocationSearch with 'replaceInHistory' will not reload the page. It will avoid page flickering and improve page performance.
            dispatch(setLocationSearch({ query: query.toString(), replaceInHistory: true }));
        } else {
            dispatch(setSearchStateFromQueryParams({ locationSearch, pageNumber: 1 }));
        }
    },
    goToSsrPage: (geo: Geo) => {
        trackElementEvent<ActivitiesSeeAllActivitiesClickEventActivitiesFieldsV1>(ActivitiesSeeAllActivitiesClickEventActivitiesManifestV1.ids, {}, {});
        if (!geo.cityId) return;
        const searchPageUrl = getSearchPageFromCityIdUrl(geo.cityId);
        dispatch(routeTo(searchPageUrl));
    },
});
